import { Component, lazy, Suspense, useEffect, type ErrorInfo, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useCartSync } from "@/hooks/useCartSync";
import { SoundtrackProvider } from "@/contexts/SoundtrackContext";
import FloatingSoundtrackIndicator from "@/components/home/FloatingSoundtrackIndicator";
import { supabase } from "@/integrations/supabase/client";
import { useCommerceCart } from "@/stores/commerceCartStore";
import ShoppingAnalytics from "@/components/ShoppingAnalytics";

// Lazy-load all non-homepage routes for better LCP
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const NewsletterPage = lazy(() => import("./pages/NewsletterPage.tsx"));
const NewsletterWelcomePage = lazy(() => import("./pages/NewsletterWelcomePage.tsx"));
const NewsletterConfirmPage = lazy(() => import("./pages/NewsletterConfirmPage.tsx"));
const OurStoryPage = lazy(() => import("./pages/OurStoryPage.tsx"));
const PhilosophyPage = lazy(() => import("./pages/PhilosophyPage.tsx"));
const IslandFirstTeacherPage = lazy(() => import("./pages/IslandFirstTeacherPage.tsx"));
const YaphenePage = lazy(() => import("./pages/brands/YaphenePage.tsx"));
const MarapaPage = lazy(() => import("./pages/brands/MarapaPage.tsx"));
const NezerPage = lazy(() => import("./pages/brands/NezerPage.tsx"));
const AdminPopupShopsPage = lazy(() => import("./pages/AdminPopupShopsPage.tsx"));
const AdminIsleOfStillnessPage = lazy(() => import("./pages/AdminIsleOfStillnessPage.tsx"));
const CalumePage = lazy(() => import("./pages/brands/CalumePage.tsx"));
const FlaurielPage = lazy(() => import("./pages/brands/FlaurielPage.tsx"));
const HerbzKulturePage = lazy(() => import("./pages/brands/HerbzKulturePage.tsx"));
const TallivoPage = lazy(() => import("./pages/brands/TallivoPage.tsx"));
const AnaElliPage = lazy(() => import("./pages/brands/AnaElliPage.tsx"));
const SunriseStillnessPage = lazy(() => import("./pages/experiences/SunriseStillnessPage.tsx"));
const BalmBondPage = lazy(() => import("./pages/experiences/BalmBondPage.tsx"));
const PerfumeBarPage = lazy(() => import("./pages/experiences/PerfumeBarPage.tsx"));
const IsleOfStillnessListenPage = lazy(() => import("./pages/IsleOfStillnessListenPage.tsx"));
const CreativeImmersionPage = lazy(() => import("./pages/experiences/CreativeImmersionPage.tsx"));
const AboutIslandPage = lazy(() => import("./pages/AboutIslandPage.tsx"));
const FAQPage = lazy(() => import("./pages/FAQPage.tsx"));
const WholesalePage = lazy(() => import("./pages/WholesalePage.tsx"));
const ContactPage = lazy(() => import("./pages/ContactPage.tsx"));
const RefundPolicyPage = lazy(() => import("./pages/RefundPolicyPage.tsx"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage.tsx"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage.tsx"));
const ShopPage = lazy(() => import("./pages/ShopPage.tsx"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage.tsx"));
const ShopNativePage = lazy(() => import("./pages/commerce/ShopNativePage.tsx"));
const ProductNativePage = lazy(() => import("./pages/commerce/ProductNativePage.tsx"));
const CartNativePage = lazy(() => import("./pages/commerce/CartNativePage.tsx"));
const CheckoutNativePage = lazy(() => import("./pages/commerce/CheckoutNativePage.tsx"));
const CheckoutNativeReturnPage = lazy(() => import("./pages/commerce/CheckoutNativeReturnPage.tsx"));
const LandingPagePublic = lazy(() => import("./pages/commerce/LandingPagePublic.tsx"));
const CommerceMerchandisingPage = lazy(() => import("./pages/admin/commerce/CommerceMerchandisingPage.tsx"));
const AccountNativePage = lazy(() => import("./pages/commerce/AccountNativePage.tsx"));
const AccountNativeOrdersPage = lazy(() => import("./pages/commerce/AccountNativeOrdersPage.tsx"));
const AccountNativeOrderDetailPage = lazy(() => import("./pages/commerce/AccountNativeOrderDetailPage.tsx"));
const AccountNativeProfilePage = lazy(() => import("./pages/commerce/AccountNativeProfilePage.tsx"));
const AccountNativeAddressesPage = lazy(() => import("./pages/commerce/AccountNativeAddressesPage.tsx"));
const AccountNativePassportPage = lazy(() => import("./pages/commerce/AccountNativePassportPage.tsx"));
const BlogPage = lazy(() => import("./pages/BlogPage.tsx"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage.tsx"));
const SocialWorkPage = lazy(() => import("./pages/SocialWorkPage.tsx"));
const CompanyProfilePage = lazy(() => import("./pages/CompanyProfilePage.tsx"));
const CompanyTimelinePage = lazy(() => import("./pages/CompanyTimelinePage.tsx"));
const PressRecognitionPage = lazy(() => import("./pages/PressRecognitionPage.tsx"));
const TestimonialsPage = lazy(() => import("./pages/TestimonialsPage.tsx"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage.tsx"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage.tsx"));
const AdminBlogPage = lazy(() => import("./pages/AdminBlogPage.tsx"));
const AdminRegistrationsPage = lazy(() => import("./pages/AdminRegistrationsPage.tsx"));
const AdminSubmissionsPage = lazy(() => import("./pages/AdminSubmissionsPage.tsx"));

const RequestSecureCheckoutPage = lazy(() => import("./pages/RequestSecureCheckoutPage.tsx"));
const AdminSecureCheckoutRequestsPage = lazy(() => import("./pages/AdminSecureCheckoutRequestsPage.tsx"));
const AdminEllaVideosPage = lazy(() => import("./pages/AdminEllaVideosPage.tsx"));
const AdminTasteContentPage = lazy(() => import("./pages/AdminTasteContentPage.tsx"));
const CommerceDashboardPage = lazy(() => import("./pages/admin/commerce/CommerceDashboardPage.tsx"));
const CommerceHealthPage = lazy(() => import("./pages/admin/commerce/CommerceHealthPage.tsx"));
const CommerceReportingPage = lazy(() => import("./pages/admin/commerce/CommerceReportingPage.tsx"));
const CommerceLaunchPage = lazy(() => import("./pages/admin/commerce/CommerceLaunchPage.tsx"));
const CommerceAlertsPage = lazy(() => import("./pages/admin/commerce/CommerceAlertsPage.tsx"));
const CommerceProductsPage = lazy(() => import("./pages/admin/commerce/CommerceProductsPage.tsx"));
const CommerceOrdersPage = lazy(() => import("./pages/admin/commerce/CommerceOrdersPage.tsx"));
const CommerceInventoryPage = lazy(() => import("./pages/admin/commerce/CommerceInventoryPage.tsx"));
const CommerceCollectionsPage = lazy(() => import("./pages/admin/commerce/CommerceCollectionsPage.tsx"));
const CommerceFulfillmentPage = lazy(() => import("./pages/admin/commerce/CommerceFulfillmentPage.tsx"));
const CommerceShippingPage = lazy(() => import("./pages/admin/commerce/CommerceShippingPage.tsx"));
const ShopifyImportPrepPage = lazy(() => import("./pages/admin/commerce/ShopifyImportPrepPage.tsx"));
const CommerceOversellPage = lazy(() => import("./pages/admin/commerce/CommerceOversellPage.tsx"));
const CommerceSyncPage = lazy(() => import("./pages/admin/commerce/CommerceSyncPage.tsx"));
const CommerceReturnsPage = lazy(() => import("./pages/admin/commerce/CommerceReturnsPage.tsx"));
const CommercePromotionsPage = lazy(() => import("./pages/admin/commerce/CommercePromotionsPage.tsx"));
const CommerceInventoryDiagnosticsPage = lazy(() => import("./pages/platform-admin/CommerceInventoryDiagnosticsPage.tsx"));
const CommerceInventoryAuthorityPage = lazy(() => import("./pages/platform-admin/CommerceInventoryAuthorityPage.tsx"));
const TrackOrderPage = lazy(() => import("./pages/TrackOrderPage.tsx"));
const MerchPage = lazy(() => import("./pages/MerchPage.tsx"));
const EventsPage = lazy(() => import("./pages/EventsPage.tsx"));
const PartnershipsPage = lazy(() => import("./pages/PartnershipsPage.tsx"));
const NaturalHairCarePage = lazy(() => import("./pages/NaturalHairCarePage.tsx"));
const CaribbeanBotanicalSkincarePage = lazy(() => import("./pages/CaribbeanBotanicalSkincarePage.tsx"));
const NaturalProductsDrySkinHairPage = lazy(() => import("./pages/NaturalProductsDrySkinHairPage.tsx"));
const CaribbeanVeganSaucesPage = lazy(() => import("./pages/CaribbeanVeganSaucesPage.tsx"));
const ShopByConcernPage = lazy(() => import("./pages/ShopByConcernPage.tsx"));
const QuizPage = lazy(() => import("./pages/QuizPage.tsx"));
const QuizResultsPage = lazy(() => import("./pages/QuizResultsPage.tsx"));
const QuizFunnelDashboard = lazy(() => import("./pages/QuizFunnelDashboard.tsx"));
const DiscoverStKittsPage = lazy(() => import("./pages/DiscoverStKittsPage.tsx"));
const ForestHeritagePage = lazy(() => import("./pages/ForestHeritagePage.tsx"));
const DiscoverCategoryPage = lazy(() => import("./pages/DiscoverCategoryPage.tsx"));
const DiscoverNaturePage = lazy(() => import("./pages/DiscoverNaturePage.tsx"));
const BotanicalArchivePage = lazy(() => import("./pages/botanical/BotanicalArchivePage.tsx"));
const BotanicalTrailPage = lazy(() => import("./pages/botanical/BotanicalTrailPage.tsx"));
const BotanicalSpeciesPage = lazy(() => import("./pages/botanical/BotanicalSpeciesPage.tsx"));
const BotanicalElevationBandPage = lazy(() => import("./pages/botanical/BotanicalElevationBandPage.tsx"));
const FestivalsTraditionsPage = lazy(() => import("./pages/FestivalsTraditionsPage.tsx"));
const BeforeYouArrivePage = lazy(() => import("./pages/BeforeYouArrivePage.tsx"));
const TasteStKittsPage = lazy(() => import("./pages/TasteStKittsPage.tsx"));
const IslandGuidesPage = lazy(() => import("./pages/IslandGuidesPage.tsx"));
const ReserveRitualPage = lazy(() => import("./pages/ReserveRitualPage.tsx"));
const DiscoverNevisPage = lazy(() => import("./pages/DiscoverNevisPage.tsx"));
const NevisChapterPage = lazy(() => import("./pages/NevisChapterPage.tsx"));
const IslandLibraryPage = lazy(() => import("./pages/IslandLibraryPage.tsx"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn.tsx"));
const ShopCheckoutPage = lazy(() => import("./pages/ShopCheckoutPage.tsx"));
const AdminShopOrdersPage = lazy(() => import("./pages/AdminShopOrdersPage.tsx"));
const AdminTransPakPage = lazy(() => import("./pages/AdminTransPakPage.tsx"));
const KittitianCulturePage = lazy(() => import("./pages/KittitianCulturePage.tsx"));
const HistoryHeritagePage = lazy(() => import("./pages/HistoryHeritagePage.tsx"));
const ArchiveIndexPage = lazy(() => import("./pages/ArchiveIndexPage.tsx"));
const CaribbeanLivingArchivePage = lazy(() => import("./pages/CaribbeanLivingArchivePage.tsx"));
const LivingArchiveCollectionPage = lazy(() => import("./pages/LivingArchiveCollectionPage.tsx"));
const StKittsBirdsPage = lazy(() => import("./pages/StKittsBirdsPage.tsx"));
const BirdWatchingPage = lazy(() => import("./pages/BirdWatchingPage.tsx"));
const StKittsBirdSpeciesPage = lazy(() => import("./pages/StKittsBirdSpeciesPage.tsx"));
const PlantGuidePage = lazy(() => import("./pages/PlantGuidePage.tsx"));
const PlantSpeciesPage = lazy(() => import("./pages/PlantSpeciesPage.tsx"));
const LivingArchiveEntryPage = lazy(() => import("./pages/LivingArchiveEntryPage.tsx"));
const FermentedBeveragesPage = lazy(() => import("./pages/FermentedBeveragesPage.tsx"));
const LaFontainePage = lazy(() => import("./pages/LaFontainePage.tsx"));
const ExhibitionsIndexPage = lazy(() => import("./pages/ExhibitionsIndexPage.tsx"));
const ExhibitionPage = lazy(() => import("./pages/ExhibitionPage.tsx"));
const KnowledgeDiscoveryPage = lazy(() => import("./pages/KnowledgeDiscoveryPage.tsx"));
const HumanExperiencesPage = lazy(() => import("./pages/HumanExperiencesPage.tsx"));
const HumanExperiencePage = lazy(() => import("./pages/HumanExperiencePage.tsx"));
const HeritageArchivePage = lazy(() => import("./pages/HeritageArchivePage.tsx"));
const SpeciesProfilePage = lazy(() => import("./pages/SpeciesProfilePage.tsx"));
const ForestStoriesPage = lazy(() => import("./pages/ForestStoriesPage.tsx"));
const ForestStoryPage = lazy(() => import("./pages/ForestStoryPage.tsx"));
const HeritageFieldNotesPage = lazy(() => import("./pages/HeritageFieldNotesPage.tsx"));
const HeritageFieldNotePage = lazy(() => import("./pages/HeritageFieldNotePage.tsx"));
const KnowledgeHoldersPage = lazy(() => import("./pages/KnowledgeHoldersPage.tsx"));
const KnowledgeHolderPage = lazy(() => import("./pages/KnowledgeHolderPage.tsx"));
const AdminDigitalOrdersPage = lazy(() => import("./pages/AdminDigitalOrdersPage.tsx"));
const AdminOrderIssuesPage = lazy(() => import("./pages/AdminOrderIssuesPage.tsx"));
const AdminLegacyArchivePage = lazy(() => import("./pages/AdminLegacyArchivePage.tsx"));
const AdminCultureArchivePage = lazy(() => import("./pages/AdminCultureArchivePage.tsx"));
const AdminFieldNotesPage = lazy(() => import("./pages/AdminFieldNotesPage.tsx"));
const AdminLanguageInsightsPage = lazy(() => import("./pages/AdminLanguageInsightsPage.tsx"));
const DownloadRedirectPage = lazy(() => import("./pages/DownloadRedirectPage.tsx"));
const VisitDiOrganicsShoppePage = lazy(() => import("./pages/VisitDiOrganicsShoppePage.tsx"));
const TourGroupWorkshopsPage = lazy(() => import("./pages/TourGroupWorkshopsPage.tsx"));
const VirtualShopPage = lazy(() => import("./pages/VirtualShopPage.tsx"));
const CaribbeanHeritageLongevityMethodPage = lazy(() => import("./pages/CaribbeanHeritageLongevityMethodPage.tsx"));
const BookingConfirmedPage = lazy(() => import("./pages/BookingConfirmedPage.tsx"));
const FieldNotesArchivePage = lazy(() => import("./pages/FieldNotesArchivePage.tsx"));
const FieldNoteArticlePage = lazy(() => import("./pages/FieldNoteArticlePage.tsx"));
const DosDanesPondFieldNotePage = lazy(() => import("./pages/DosDanesPondFieldNotePage.tsx"));
const PathwayPage = lazy(() => import("./pages/PathwayPage.tsx"));
const WellnessRhythmQuizPage = lazy(() => import("./pages/WellnessRhythmQuizPage.tsx"));
const StoryOfMoneyPage = lazy(() => import("./pages/discover/StoryOfMoneyPage.tsx"));
const MusicStKittsNevisPage = lazy(() => import("./pages/MusicStKittsNevisPage.tsx"));
const WellnessRhythmResultPage = lazy(() => import("./pages/WellnessRhythmResultPage.tsx"));
const CommunityPage = lazy(() => import("./pages/CommunityPage.tsx"));
const ExperienceLikeALocalPage = lazy(() => import("./pages/ExperienceLikeALocalPage.tsx"));
const CaribbeanLegacyHousePage = lazy(() => import("./pages/CaribbeanLegacyHousePage.tsx"));
const LiamuigaChapterPage = lazy(() => import("./pages/LiamuigaChapterPage.tsx"));
const LivingLegacyChapterPlaceholderPage = lazy(() => import("./pages/LivingLegacyChapterPlaceholderPage.tsx"));
const MuseumGalleryRoomPage = lazy(() => import("./pages/museum/GalleryRoomPage.tsx"));
const MuseumCollectionPage = lazy(() => import("./pages/museum/CollectionPage.tsx"));
const MuseumCollectionItemPage = lazy(() => import("./pages/museum/CollectionItemPage.tsx"));
const MuseumGuidesIndex = lazy(() => import("./pages/museum/GuidesPage.tsx").then(m => ({ default: m.GuidesIndexPage })));
const MuseumGuide = lazy(() => import("./pages/museum/GuidesPage.tsx").then(m => ({ default: m.GuidePage })));
const MuseumCuratorPage = lazy(() => import("./pages/museum/CuratorPage.tsx"));
const MuseumLibraryPage = lazy(() => import("./pages/museum/LibraryPage.tsx"));
const MuseumJournalIndex = lazy(() => import("./pages/museum/JournalPage.tsx").then(m => ({ default: m.JournalIndexPage })));
const MuseumJournalArticle = lazy(() => import("./pages/museum/JournalPage.tsx").then(m => ({ default: m.JournalArticlePage })));
const MuseumContinuePage = lazy(() => import("./pages/museum/ContinuePage.tsx"));
const SugarTownEstatePage = lazy(() => import("./pages/SugarTownEstatePage.tsx"));
const RootedRitualPage = lazy(() => import("./pages/RootedRitualPage.tsx"));
const RootedJourneysPage = lazy(() => import("./pages/rooted/RootedJourneysPage.tsx"));
const RootedRootsPage = lazy(() => import("./pages/rooted/RootedRootsPage.tsx"));
const RootedMusicPage = lazy(() => import("./pages/rooted/RootedMusicPage.tsx"));
const RootedHeritagePage = lazy(() => import("./pages/rooted/RootedHeritagePage.tsx"));
const RootedFaqPage = lazy(() => import("./pages/rooted/RootedFaqPage.tsx"));
const RootedWomensSeasonsPage = lazy(() => import("./pages/rooted/RootedWomensSeasonsPage.tsx"));
const NatureAsTeacherPage = lazy(() => import("./pages/NatureAsTeacherPage.tsx"));
const CaribbeanPoshWellnessInnovationIconAward2026Page = lazy(() => import("./pages/CaribbeanPoshWellnessInnovationIconAward2026Page.tsx"));
const LegacyImpactPage = lazy(() => import("./pages/LegacyImpactPage.tsx"));
const MeetTheFounderPage = lazy(() => import("./pages/MeetTheFounderPage.tsx"));
const MentorshipAdvisoryPage = lazy(() => import("./pages/MentorshipAdvisoryPage.tsx"));
const IntegrativeWellnessIntensivePage = lazy(() => import("./pages/IntegrativeWellnessIntensivePage.tsx"));
const AdminMentorshipPage = lazy(() => import("./pages/AdminMentorshipPage.tsx"));
const AdminHeritageMemoriesPage = lazy(() => import("./pages/AdminHeritageMemoriesPage.tsx"));
const RootRewardsNetworkPage = lazy(() => import("./pages/RootRewardsNetworkPage.tsx"));
const RootAccountPage = lazy(() => import("./pages/RootAccountPage.tsx"));
const AdminRootsPage = lazy(() => import("./pages/AdminRootsPage.tsx"));
const KnowledgeLibraryPage = lazy(() => import("./pages/knowledge/KnowledgeLibraryPage.tsx"));
const KnowledgeCategoryPage = lazy(() => import("./pages/knowledge/KnowledgeCategoryPage.tsx"));
const KnowledgeArticlePage = lazy(() => import("./pages/knowledge/KnowledgeArticlePage.tsx"));
const AdminKnowledgePage = lazy(() => import("./pages/AdminKnowledgePage.tsx"));
// Professionally hand-translated Mandarin (中文) pages
const ZhOurStoryPage = lazy(() => import("./pages/zh/ZhOurStoryPage.tsx"));
const ZhLegacyImpactPage = lazy(() => import("./pages/zh/ZhLegacyImpactPage.tsx"));
const ZhVisitDiOrganicsShoppePage = lazy(() => import("./pages/zh/ZhVisitDiOrganicsShoppePage.tsx"));
const ZhBalmBondPage = lazy(() => import("./pages/zh/ZhBalmBondPage.tsx"));
const ZhSunriseStillnessPage = lazy(() => import("./pages/zh/ZhSunriseStillnessPage.tsx"));
const ZhDiscoverStKittsPage = lazy(() => import("./pages/zh/ZhDiscoverStKittsPage.tsx"));
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";

const queryClient = new QueryClient();
const lazyReloadStorageKey = "sugar-town:lazy-route-reload-attempted-at";

const isLazyImportError = (error: unknown) =>
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Load failed/i.test(
    error instanceof Error ? error.message : String(error),
  );

class LazyRouteErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  componentDidCatch(error: Error, _errorInfo: ErrorInfo) {
    if (isLazyImportError(error)) {
      const lastAttempt = Number(sessionStorage.getItem(lazyReloadStorageKey) || 0);
      const shouldReload = Date.now() - lastAttempt > 10000;

      if (shouldReload) {
        sessionStorage.setItem(lazyReloadStorageKey, String(Date.now()));
        window.location.reload();
        return;
      }
    }

    this.setState({ hasError: true });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
          <div className="max-w-md text-center">
            <h1 className="font-serif text-3xl mb-3">Page update needed</h1>
            <p className="font-sans text-muted-foreground mb-6">
              Please refresh this page to load the latest version.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="font-sans text-sm bg-primary text-primary-foreground px-5 py-3 rounded-md hover:bg-primary/90 transition-colors"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const AppContent = () => {
  useCartSync();
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") {
        // Defer to avoid running inside the auth callback.
        setTimeout(() => {
          void useCommerceCart.getState().mergeOnSignIn();
        }, 0);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);
  return null;
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <SoundtrackProvider>
        <BrowserRouter>
          <AppContent />
          <ShoppingAnalytics />
          <PaymentTestModeBanner />
        <LazyRouteErrorBoundary>
          <Suspense fallback={<div className="min-h-screen" />}>
            <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/newsletter" element={<NewsletterPage />} />
            <Route path="/newsletter/welcome" element={<NewsletterWelcomePage />} />
            <Route path="/newsletter/confirm" element={<NewsletterConfirmPage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/product/:handle" element={<ProductDetailPage />} />
            {/* Native commerce engine (Phase 1 — built alongside the live Shopify storefront) */}
            <Route path="/shop-native" element={<ShopNativePage />} />
            <Route path="/product-native/:slug" element={<ProductNativePage />} />
            <Route path="/cart-native" element={<CartNativePage />} />
            <Route path="/checkout-native" element={<CheckoutNativePage />} />
            <Route path="/checkout-native/return" element={<CheckoutNativeReturnPage />} />
            <Route path="/lp/:slug" element={<LandingPagePublic />} />
            <Route path="/account-native" element={<AccountNativePage />} />
            <Route path="/account-native/profile" element={<AccountNativeProfilePage />} />
            <Route path="/account-native/addresses" element={<AccountNativeAddressesPage />} />
            <Route path="/account-native/passport" element={<AccountNativePassportPage />} />
            <Route path="/account-native/orders" element={<AccountNativeOrdersPage />} />
            <Route path="/account-native/orders/:orderId" element={<AccountNativeOrderDetailPage />} />
            {/* Shopify-style aliases so external links (Instagram bio, Linkinbio, old Shopify URLs) don't 404 */}
            <Route path="/products/:handle" element={<ProductDetailPage />} />
            <Route path="/products" element={<Navigate to="/shop" replace />} />
            <Route path="/collections" element={<Navigate to="/shop" replace />} />
            <Route path="/collections/:handle" element={<Navigate to="/shop" replace />} />
            <Route path="/cart" element={<Navigate to="/shop" replace />} />
            <Route path="/checkout" element={<Navigate to="/shop" replace />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blogs/natural-products/blog" element={<Navigate to="/blog" replace />} />
            <Route path="/blog/experience-real-kittitian-culture-beyond-resorts" element={<Navigate to="/discover-st-kitts/experience-it-like-a-local" replace />} />
            <Route path="/blog/:slug" element={<BlogArticlePage />} />
            <Route path="/blog/la-fontaine-fountain-estate-st-kitts" element={<LaFontainePage />} />
            <Route path="/our-story" element={<OurStoryPage />} />
            <Route path="/philosophy" element={<PhilosophyPage />} />
            <Route path="/island-first-teacher" element={<IslandFirstTeacherPage />} />
            <Route path="/brands/yaphene" element={<YaphenePage />} />
            <Route path="/brands/marapa" element={<MarapaPage />} />
            <Route path="/brands/nezer" element={<NezerPage />} />
            <Route path="/brands/calume" element={<CalumePage />} />
            <Route path="/brands/flauriel" element={<FlaurielPage />} />
            <Route path="/brands/herbz-kulture" element={<HerbzKulturePage />} />
            <Route path="/brands/tallivo" element={<TallivoPage />} />
            <Route path="/brands/anaelli" element={<AnaElliPage />} />
            <Route path="/experiences/sunrise-and-stillness" element={<SunriseStillnessPage />} />
            <Route path="/experiences/balm-and-bond" element={<BalmBondPage />} />
            <Route path="/experiences/perfume-bar" element={<PerfumeBarPage />} />
            <Route path="/isle-of-stillness/listen" element={<IsleOfStillnessListenPage />} />
            <Route path="/booking-confirmed" element={<BookingConfirmedPage />} />
            <Route path="/experiences/creative-immersion" element={<CreativeImmersionPage />} />
            <Route path="/merch" element={<MerchPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/about-our-island" element={<AboutIslandPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/wholesale" element={<WholesalePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/social-work" element={<SocialWorkPage />} />
            <Route path="/company-profile" element={<CompanyProfilePage />} />
            <Route path="/company-timeline" element={<CompanyTimelinePage />} />
            <Route path="/press-and-recognition" element={<PressRecognitionPage />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/admin" element={<AdminBlogPage />} />
            <Route path="/admin/registrations" element={<AdminRegistrationsPage />} />
            <Route path="/admin/submissions" element={<AdminSubmissionsPage />} />

            <Route path="/admin/ella-videos" element={<AdminEllaVideosPage />} />
            <Route path="/admin/taste-content" element={<AdminTasteContentPage />} />
            <Route path="/platform-admin/commerce-inventory" element={<CommerceInventoryDiagnosticsPage />} />
            <Route path="/platform-admin/inventory-authority" element={<CommerceInventoryAuthorityPage />} />
            <Route path="/track-order" element={<TrackOrderPage />} />
            <Route path="/partnerships" element={<PartnershipsPage />} />
            <Route path="/natural-hair-care-black-women" element={<NaturalHairCarePage />} />
            <Route path="/caribbean-botanical-skincare" element={<CaribbeanBotanicalSkincarePage />} />
            <Route path="/natural-products-dry-skin-hair" element={<NaturalProductsDrySkinHairPage />} />
            <Route path="/caribbean-vegan-sauces" element={<CaribbeanVeganSaucesPage />} />
            <Route path="/shop-by-concern" element={<ShopByConcernPage />} />
            <Route path="/quiz" element={<QuizPage />} />
            <Route path="/quiz/results/:slug" element={<QuizResultsPage />} />
            <Route path="/quiz/analytics" element={<QuizFunnelDashboard />} />
            <Route path="/discover-st-kitts" element={<DiscoverStKittsPage />} />
            <Route path="/forest-heritage" element={<ForestHeritagePage />} />
            <Route path="/forest-heritage-of-st-kitts" element={<ForestHeritagePage />} />
            <Route path="/rooted-ritual" element={<RootedRitualPage />} />
            <Route path="/rooted-ritual/journeys" element={<RootedJourneysPage />} />
            <Route path="/rooted-ritual/roots" element={<RootedRootsPage />} />
            <Route path="/rooted-ritual/music" element={<RootedMusicPage />} />
            <Route path="/rooted-ritual/heritage" element={<RootedHeritagePage />} />
            <Route path="/rooted-ritual/faq" element={<RootedFaqPage />} />
            <Route path="/rooted-ritual/womens-seasons" element={<RootedWomensSeasonsPage />} />
            {/* Canonical ecosystem entry routes — Rooted Ritual is the entry layer into the wider Sugar Town Organics ecosystem */}
            <Route path="/journeys" element={<Navigate to="/rooted-ritual/journeys" replace />} />
            <Route path="/music" element={<Navigate to="/rooted-ritual/music" replace />} />
            <Route path="/wellness-passport" element={<Navigate to="/rooted-ritual/roots" replace />} />
            <Route path="/botanical-encyclopedia" element={<Navigate to="/heritage-archive" replace />} />
            <Route path="/nature-as-teacher" element={<NatureAsTeacherPage />} />
            <Route path="/discover-st-kitts/taste-of-st-kitts" element={<TasteStKittsPage />} />
            <Route path="/discover-st-kitts/curious-visitor-faq" element={<BeforeYouArrivePage />} />
            <Route path="/discover-st-kitts/before-you-arrive" element={<BeforeYouArrivePage />} />
            <Route path="/discover-st-kitts/festivals-traditions" element={<FestivalsTraditionsPage />} />
            <Route path="/discover-st-kitts/festivals-and-traditions" element={<FestivalsTraditionsPage />} />
            <Route path="/discover-st-kitts/experience-it-like-a-local" element={<ExperienceLikeALocalPage />} />
            <Route path="/discover-st-kitts/nature-geography" element={<DiscoverNaturePage />} />
            <Route path="/discover-st-kitts/nature" element={<DiscoverNaturePage />} />
            <Route path="/discover-st-kitts/history-of-money" element={<StoryOfMoneyPage />} />
            <Route path="/discover-st-kitts/currency-history" element={<StoryOfMoneyPage />} />
            <Route path="/discover/music-in-st-kitts-nevis" element={<MusicStKittsNevisPage />} />
            <Route path="/discover-st-kitts/music" element={<MusicStKittsNevisPage />} />
            <Route path="/discover-st-kitts/music-in-st-kitts-nevis" element={<MusicStKittsNevisPage />} />
            <Route path="/discover-st-kitts/nature-geography/species/:slug" element={<BotanicalSpeciesPage />} />
            <Route path="/discover-st-kitts/nature-geography/:archive/band/:band" element={<BotanicalElevationBandPage />} />
            <Route path="/discover-st-kitts/nature-geography/:archive/:trail" element={<BotanicalTrailPage />} />
            <Route path="/discover-st-kitts/nature-geography/:archive" element={<BotanicalArchivePage />} />
            <Route path="/discover-st-kitts/:category" element={<DiscoverCategoryPage />} />

            <Route path="/taste-st-kitts" element={<TasteStKittsPage />} />
            <Route path="/island-library" element={<IslandLibraryPage />} />
            <Route path="/island-flavors" element={<TasteStKittsPage />} />
            <Route path="/st-kitts-faq" element={<BeforeYouArrivePage />} />
            <Route path="/nature-of-st-kitts" element={<DiscoverNaturePage />} />
            <Route path="/history-of-st-kitts" element={<HistoryHeritagePage />} />
            <Route path="/st-kitts-festivals" element={<FestivalsTraditionsPage />} />
            <Route path="/local-st-kitts" element={<ExperienceLikeALocalPage />} />
            <Route path="/st-kitts-rum-guide" element={<DiscoverCategoryPage categorySlug="history-heritage" />} />
            <Route path="/discover-nevis" element={<DiscoverNevisPage />} />
            <Route path="/discover-nevis/:slug" element={<NevisChapterPage />} />
            <Route path="/island-guides" element={<IslandGuidesPage />} />
            <Route path="/reserve-a-ritual" element={<ReserveRitualPage />} />
            <Route path="/checkout/return" element={<CheckoutReturn />} />
            <Route path="/shop/checkout" element={<ShopCheckoutPage />} />
            <Route path="/admin/shop-orders" element={<AdminShopOrdersPage />} />
            <Route path="/admin/transpak" element={<AdminTransPakPage />} />
            <Route path="/admin/pop-up-shops" element={<AdminPopupShopsPage />} />
            <Route path="/admin/isle-of-stillness" element={<AdminIsleOfStillnessPage />} />
            <Route path="/download" element={<DownloadRedirectPage />} />
            <Route path="/visit-di-organics-shoppe" element={<VisitDiOrganicsShoppePage />} />
            <Route path="/visit" element={<Navigate to="/visit-di-organics-shoppe" replace />} />
            <Route path="/tour-group-workshops" element={<TourGroupWorkshopsPage />} />
            <Route path="/experiences/balm-bond" element={<Navigate to="/experiences/balm-and-bond" replace />} />
            <Route path="/balm-and-bond" element={<Navigate to="/experiences/balm-and-bond" replace />} />
            <Route path="/balm-and-bond-experiences" element={<Navigate to="/experiences/balm-and-bond" replace />} />
            <Route path="/virtual-shop" element={<VirtualShopPage />} />
            <Route path="/caribbean-heritage-longevity-method" element={<CaribbeanHeritageLongevityMethodPage />} />
            <Route path="/kittitian-culture" element={<KittitianCulturePage />} />
            <Route path="/history-and-heritage" element={<HistoryHeritagePage />} />
            <Route path="/walk-through-kittitian-time" element={<HistoryHeritagePage />} />
            <Route path="/archive" element={<HeritageArchivePage />} />
            <Route path="/legacy-archive-index" element={<ArchiveIndexPage />} />
            <Route path="/living-archive" element={<CaribbeanLivingArchivePage />} />
            <Route path="/living-archive/fermented-beverages-caribbean" element={<FermentedBeveragesPage />} />
            <Route path="/living-archive/la-fontaine-fountain-estate-st-kitts" element={<LaFontainePage />} />
            <Route path="/living-archive/foods/fermented-beverages-caribbean" element={<Navigate to="/living-archive/fermented-beverages-caribbean" replace />} />
            <Route path="/discover" element={<KnowledgeDiscoveryPage />} />
            <Route path="/living-archive/discover" element={<KnowledgeDiscoveryPage />} />
            <Route path="/living-archive/exhibitions" element={<ExhibitionsIndexPage />} />
            <Route path="/living-archive/exhibitions/:slug" element={<ExhibitionPage />} />
            <Route path="/living-archive/birds" element={<StKittsBirdsPage />} />
            <Route path="/living-archive/birds/watching" element={<BirdWatchingPage />} />
            <Route path="/living-archive/birds/:slug" element={<StKittsBirdSpeciesPage />} />
            <Route path="/plant-guide" element={<PlantGuidePage />} />
            <Route path="/plant-guide/:slug" element={<PlantSpeciesPage />} />
            <Route path="/living-archive/:collection" element={<LivingArchiveCollectionPage />} />
            <Route path="/living-archive/:collection/:slug" element={<LivingArchiveEntryPage />} />
            <Route path="/human-experiences" element={<HumanExperiencesPage />} />
            <Route path="/human-experiences/:slug" element={<HumanExperiencePage />} />
            <Route path="/heritage-archive" element={<HeritageArchivePage />} />
            <Route path="/heritage-archive/stories" element={<ForestStoriesPage />} />
            <Route path="/heritage-archive/stories/:slug" element={<ForestStoryPage />} />
            <Route path="/heritage-archive/field-notes" element={<HeritageFieldNotesPage />} />
            <Route path="/heritage-archive/field-notes/:slug" element={<HeritageFieldNotePage />} />
            <Route path="/heritage-archive/knowledge-holders" element={<KnowledgeHoldersPage />} />
            <Route path="/heritage-archive/knowledge-holders/:slug" element={<KnowledgeHolderPage />} />
            <Route path="/heritage-archive/:slug" element={<SpeciesProfilePage />} />
            <Route path="/field-notes" element={<FieldNotesArchivePage />} />
            <Route path="/field-notes/:slug" element={<FieldNoteArticlePage />} />
            <Route path="/journal/dos-danes-pond" element={<DosDanesPondFieldNotePage />} />
            <Route path="/field-notes-st-kitts/dos-danes-pond-vol-1" element={<DosDanesPondFieldNotePage />} />
            <Route path="/pathways/:slug" element={<PathwayPage />} />
            <Route path="/rhythm" element={<WellnessRhythmQuizPage />} />
            <Route path="/rhythm/:slug" element={<WellnessRhythmResultPage />} />
            <Route path="/admin/digital-orders" element={<AdminDigitalOrdersPage />} />
            <Route path="/admin/order-issues" element={<AdminOrderIssuesPage />} />
            <Route path="/admin/legacy-archive" element={<AdminLegacyArchivePage />} />
            <Route path="/admin/culture-archive" element={<AdminCultureArchivePage />} />
            <Route path="/admin/field-notes" element={<AdminFieldNotesPage />} />
            <Route path="/admin/language-insights" element={<AdminLanguageInsightsPage />} />
            <Route path="/admin/roots" element={<AdminRootsPage />} />
            <Route path="/admin/commerce" element={<CommerceDashboardPage />} />
            <Route path="/admin/commerce/health" element={<CommerceHealthPage />} />
            <Route path="/admin/commerce/products" element={<CommerceProductsPage />} />
            <Route path="/admin/commerce/orders" element={<CommerceOrdersPage />} />
            <Route path="/admin/commerce/inventory" element={<CommerceInventoryPage />} />
            <Route path="/admin/commerce/oversell" element={<CommerceOversellPage />} />
            <Route path="/admin/commerce/returns" element={<CommerceReturnsPage />} />
            <Route path="/admin/commerce/reporting" element={<CommerceReportingPage />} />
            <Route path="/admin/commerce/launch" element={<CommerceLaunchPage />} />
            <Route path="/admin/commerce/alerts" element={<CommerceAlertsPage />} />
            <Route path="/admin/commerce/sync" element={<CommerceSyncPage />} />
            <Route path="/admin/commerce/collections" element={<CommerceCollectionsPage />} />
            <Route path="/admin/commerce/fulfillment" element={<CommerceFulfillmentPage />} />
            <Route path="/admin/commerce/shipping" element={<CommerceShippingPage />} />
            <Route path="/admin/commerce/shopify-import" element={<ShopifyImportPrepPage />} />
            <Route path="/admin/commerce/merchandising" element={<CommerceMerchandisingPage />} />
            <Route path="/admin/commerce/promotions" element={<CommercePromotionsPage />} />
            <Route path="/admin/secure-checkout-requests" element={<AdminSecureCheckoutRequestsPage />} />
            <Route path="/request-secure-checkout" element={<RequestSecureCheckoutPage />} />
            <Route path="/root-rewards-network" element={<RootRewardsNetworkPage />} />
            <Route path="/account/roots" element={<RootAccountPage />} />
            {/* Knowledge Library — AI-search, question-first editorial */}
            <Route path="/learn" element={<KnowledgeLibraryPage />} />
            <Route path="/learn/:category" element={<KnowledgeCategoryPage />} />
            <Route path="/learn/:category/:slug" element={<KnowledgeArticlePage />} />
            <Route path="/admin/knowledge" element={<AdminKnowledgePage />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/caribbean-legacy-house" element={<CaribbeanLegacyHousePage />} />
            <Route path="/liamuiga" element={<LiamuigaChapterPage />} />
            <Route path="/liamuiga/galleries/:slug" element={<MuseumGalleryRoomPage />} />
            <Route path="/liamuiga/collection" element={<MuseumCollectionPage />} />
            <Route path="/liamuiga/collection/:category" element={<MuseumCollectionPage />} />
            <Route path="/liamuiga/collection/:category/:id" element={<MuseumCollectionItemPage />} />
            <Route path="/liamuiga/guides" element={<MuseumGuidesIndex />} />
            <Route path="/liamuiga/guides/:slug" element={<MuseumGuide />} />
            <Route path="/liamuiga/curator" element={<MuseumCuratorPage />} />
            <Route path="/liamuiga/library" element={<MuseumLibraryPage />} />
            <Route path="/liamuiga/journal" element={<MuseumJournalIndex />} />
            <Route path="/liamuiga/journal/:slug" element={<MuseumJournalArticle />} />
            <Route path="/liamuiga/continue" element={<MuseumContinuePage />} />
            <Route path="/living-legacy-of-liamuiga" element={<LiamuigaChapterPage />} />
            <Route path="/living-legacy-of-liamuiga/chapter-one" element={<LiamuigaChapterPage />} />
            <Route path="/living-legacy-of-liamuiga/:slug" element={<LivingLegacyChapterPlaceholderPage />} />
            <Route path="/estate" element={<SugarTownEstatePage />} />
            <Route path="/the-estate" element={<Navigate to="/estate" replace />} />
            <Route path="/caribbean-posh-wellness-innovation-icon-award-2026" element={<CaribbeanPoshWellnessInnovationIconAward2026Page />} />
            <Route path="/legacy-impact" element={<LegacyImpactPage />} />
            <Route path="/meet-the-founder" element={<MeetTheFounderPage />} />
            <Route path="/mentorship-advisory" element={<MentorshipAdvisoryPage />} />
            <Route path="/integrative-wellness-intensive" element={<IntegrativeWellnessIntensivePage />} />
            <Route path="/admin/mentorship" element={<AdminMentorshipPage />} />
            <Route path="/admin/community-memories" element={<AdminHeritageMemoriesPage />} />
            {/* Mandarin (中文) hand-translated pages */}
            <Route path="/zh/our-story" element={<ZhOurStoryPage />} />
            <Route path="/zh/legacy-impact" element={<ZhLegacyImpactPage />} />
            <Route path="/zh/visit-di-organics-shoppe" element={<ZhVisitDiOrganicsShoppePage />} />
            <Route path="/zh/experiences/balm-and-bond" element={<ZhBalmBondPage />} />
            <Route path="/zh/experiences/sunrise-and-stillness" element={<ZhSunriseStillnessPage />} />
            <Route path="/zh/discover-st-kitts" element={<ZhDiscoverStKittsPage />} />
            <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </LazyRouteErrorBoundary>
          <FloatingSoundtrackIndicator />
      </BrowserRouter>
        </SoundtrackProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
